import React from "react";
import { InputSwitch } from "primereact/inputswitch";
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from "react-hook-form";
import { CSSProperties } from "react";
import styles from "./DefaultToggle.module.scss";
import { classNames } from "primereact/utils";
import {
  IfFalse,
  IfTrue,
  RenderWhen,
} from "../../../../infrastructure/components/RenderWhen";
import { useAppContext } from "../../../../../AppContext";
import { AppTooltip } from "../../../tooltip/AppTooltip";

import { dvSize } from "../../../../infrastructure/utilities/viewportUnitsUtility";
interface IDefaultToggleProps {
  name: string;
  label: string | React.ReactNode;
  disabled?: boolean;
  className?: string;
  styles?: CSSProperties;
  required?: boolean;
  width?: number | string;
  tooltip?: string;
  maxWidth?: string | number;
  onChange?: (checked: boolean) => void;
  standalone?: boolean;
  checked?: boolean;
}

export const DefaultToggle = ({
  name,
  label,
  disabled = false,
  className,
  styles: customStyles,
  required,
  width,
  tooltip,
  maxWidth,
  onChange,
  standalone = false,
  checked,
}: IDefaultToggleProps) => {
  const { isDesktopOrTablet } = useAppContext();
  const formContext = useFormContext();

  if (!width) {
    if (isDesktopOrTablet) {
      width = "600px";
    } else {
      width = dvSize("80dvw");
    }
  }

  const onStandaloneChange = (e: { value: boolean }) => {
    if (onChange) {
      onChange(e.value);
    }
  };

  const onFieldChange = (
    field: ControllerRenderProps<FieldValues, string>,
    e: { value: boolean },
  ) => {
    field.onChange(e.value);

    if (onChange) {
      onChange(e.value);
    }
  };

  if (standalone) {
    return (
      <div
        className={classNames(styles.root, className)}
        style={{
          ...customStyles,
          width,
          maxWidth,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <InputSwitch
          inputId={name}
          checked={checked || false}
          onChange={onStandaloneChange}
          disabled={disabled}
        />

        <RenderWhen condition={typeof label === "string"}>
          <IfTrue>
            <label
              htmlFor={name}
              className={styles.root_label}
              style={{
                textAlign: "left",
              }}
            >
              {required && <span style={{ color: "red" }}>*</span>}
              {label}
              <AppTooltip className={styles.root_tooltip} content={tooltip} />
            </label>
          </IfTrue>
          <IfFalse>
            <span>{label}</span>
          </IfFalse>
        </RenderWhen>
      </div>
    );
  }

  if (!formContext) {
    throw new Error(
      "DefaultToggle must be used within a FormProvider when standalone is false",
    );
  }

  return (
    <Controller
      name={name}
      control={formContext.control}
      render={({ field, fieldState }) => (
        <div
          className={classNames(styles.root, className)}
          style={{ ...customStyles, width }}
        >
          <InputSwitch
            inputId={name}
            checked={field.value || false}
            onChange={(e) => onFieldChange(field, e)}
            disabled={disabled}
          />

          <RenderWhen condition={typeof label === "string"}>
            <IfTrue>
              <label
                htmlFor={name}
                className={classNames(styles.root_label, {
                  [styles.error]: !!fieldState.error?.message,
                })}
                style={{
                  cursor: disabled ? "not-allowed" : "pointer",
                  textAlign: "left",
                }}
              >
                {required && <span style={{ color: "red" }}>*</span>}
                {label}
                <AppTooltip className={styles.root_tooltip} content={tooltip} />
              </label>
            </IfTrue>
            <IfFalse>
              <span
                className={classNames({
                  [styles.error]: !!fieldState.error?.message,
                })}
              >
                {label}
              </span>
            </IfFalse>
          </RenderWhen>
        </div>
      )}
    />
  );
};
